// Settings › CRM 2.0 › Customer Consent › Terms (M1-F6) — as prototype A21t (kTermsTab / kPanel).
// Legal writes terms on a consent point; the Legal Head approves and makes them
// Live; Stop asking puts them back to Draft. Changing the text of approved terms
// makes a new version (Draft) for the Legal Head.
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Drawer, MenuItem, Radio, RadioGroup, FormControlLabel, Switch, Checkbox, TextField } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost, successTost } from "../../../Services/Toast";
import {
  getConsentPoints, getConsentTerms, getConsentTerm, createConsentTerm, saveConsentTerm, moveConsentTerm, getCrm2Projects, consentError,
} from "../../../Services/Crm2Consent";
import cssClasses from "../Settings/Crm2Settings.module.css";

const HOW = { TICK: "I agree tick", TICK_OTP: "I agree tick + OTP" };
const WHO = { CONTACT_PERSON: "Portal contact person", PRIMARY_APPLICANT: "Primary applicant", EVERY_APPLICANT: "Every applicant" };
const ASKED = { ONCE_PER_CUSTOMER: "Once per customer", ONCE_PER_BOOKING: "Once per booking", EVERY_TIME: "Every time" };
const STATUS = { DRAFT: "Draft", APPROVED: "Legal Head approved", LIVE: "Live" };
const EDITOR_TOOLBAR = ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "undo", "redo"];
const PANEL_TABS = ["Terms text", "Where and who", "Projects and version"];
const NEW_TERM = { title: "", content: "", pointKey: "", how: "TICK_OTP", who: "CONTACT_PERSON", asked: "ONCE_PER_BOOKING", askAgain: false, projectIds: [] };

const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const inputSx = { "& .MuiInputBase-input": { fontSize: "1.4rem" } };

function useAllowed(key) {
  const user = useSelector((state) => state.Auth.user);
  const node = user?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Settings › Customer Consent"];
  return user?.roleId?.level == 1 || !!(node?.isAccessible && node?.[key]?.isAccessible);
}

// Prototype KPILL: ● Live · ○ Draft · ◐ Legal Head approved
const Pill = ({ status }) => {
  const live = status === "LIVE", draft = status === "DRAFT";
  return (
    <span style={{ fontSize: "1.2rem", fontFamily: "Inter-Medium", whiteSpace: "nowrap", borderRadius: "1.6rem", padding: "0.2rem 1rem",
      color: live ? "#027A48" : draft ? "#344054" : "#B54708", background: live ? "#ECFDF3" : draft ? "#F2F4F7" : "#FFFAEB" }}>
      {live ? "●" : draft ? "○" : "◐"} {STATUS[status] || STATUS.DRAFT}
    </span>
  );
};
const chip = (text, color, background) => (
  <span style={{ fontSize: "1.2rem", fontFamily: "Inter-Medium", color, background, borderRadius: "1.6rem", padding: "0.2rem 1rem", whiteSpace: "nowrap" }}>{text}</span>
);
const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

function Crm2ConsentTerms() {
  const canEdit = useAllowed("Edit terms and Ask now");
  const canApprove = useAllowed("Approve and make Live");
  const [points, setPoints] = useState([]);
  const [projects, setProjects] = useState([]);
  const [terms, setTerms] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [filters, setFilters] = useState({ pointKey: "", status: "", q: "" });
  const [panel, setPanel] = useState(null); // { term (saved or null), draft, tab, errors }
  const [busy, setBusy] = useState(false);

  const pointName = useMemo(() => Object.fromEntries(points.map((p) => [p.key, p.name])), [points]);
  const projectName = useMemo(() => Object.fromEntries(projects.map((p) => [p.projectId, p.projectName])), [projects]);

  const load = () =>
    getConsentTerms()
      .then((list) => {
        setTerms(list);
        setLoadError("");
      })
      .catch((err) => {
        console.error("[crm2][Crm2ConsentTerms] load failed", { status: err?.response?.status, message: consentError(err).message });
        setLoadError(consentError(err).message);
      });

  useEffect(() => {
    load();
    getConsentPoints()
      .then(setPoints)
      .catch((err) => console.error("[crm2][Crm2ConsentTerms] points load failed", { message: consentError(err).message }));
    getCrm2Projects()
      .then(setProjects)
      .catch((err) => console.error("[crm2][Crm2ConsentTerms] projects load failed", { message: err.message }));
  }, []);

  const shown = (terms || []).filter(
    (t) =>
      (!filters.pointKey || t.pointKey === filters.pointKey) &&
      (!filters.status || t.status === filters.status) &&
      (!filters.q || `${t.title} ${stripHtml(t.content)}`.toLowerCase().includes(filters.q.toLowerCase()))
  );
  const live = (terms || []).filter((t) => t.status === "LIVE").length;
  const approved = (terms || []).filter((t) => t.status === "APPROVED").length;

  const openNew = () => setPanel({ term: null, draft: { ...NEW_TERM, pointKey: points.find((p) => p.isActive !== false)?.key || "" }, tab: PANEL_TABS[0], errors: {} });
  const openEdit = async (t) => {
    try {
      const full = await getConsentTerm(t.id);
      setPanel({ term: full, draft: { ...full }, tab: PANEL_TABS[0], errors: {} });
    } catch (err) {
      errorTost(consentError(err).message);
    }
  };
  const setDraft = (patch) => setPanel((p) => ({ ...p, draft: { ...p.draft, ...patch } }));

  const save = async () => {
    const d = panel.draft;
    if (!d.title.trim() || !stripHtml(d.content)) {
      errorTost("Enter a title and the terms text");
      return;
    }
    const body = {
      title: d.title, content: d.content, pointKey: d.pointKey, how: d.how, who: d.who, asked: d.asked,
      askAgain: !!d.askAgain, projectIds: d.projectIds || [], lastUpdatedAt: panel.term?.updatedAt,
    };
    setBusy(true);
    try {
      const saved = panel.term ? await saveConsentTerm(panel.term.id, body) : await createConsentTerm(body);
      console.log("[crm2][Crm2ConsentTerms] saved", { id: saved.id, version: saved.version, status: saved.status });
      successTost("Terms saved");
      setPanel(null);
      load();
    } catch (err) {
      const { message, fields } = consentError(err);
      console.warn("[crm2][Crm2ConsentTerms] save refused", { message, fields });
      setPanel((p) => ({ ...p, errors: fields }));
      errorTost(message);
    } finally {
      setBusy(false);
    }
  };

  const move = async (step, okText) => {
    setBusy(true);
    try {
      const t = await moveConsentTerm(panel.term.id, step);
      console.log("[crm2][Crm2ConsentTerms] moved", { id: t.id, step, status: t.status });
      successTost(okText);
      const full = await getConsentTerm(t.id);
      setPanel((p) => ({ ...p, term: full, draft: { ...full } }));
      load();
    } catch (err) {
      errorTost(consentError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const select = (value, onChange, label, options) => (
    <TextField select size="small" value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: "18rem", ...inputSx }}
      SelectProps={{ displayEmpty: true, renderValue: (v) => `${label}: ${v ? options.find(([k]) => k === v)?.[1] : "All"}` }}>
      <MenuItem value="" sx={{ fontSize: "1.4rem" }}>All</MenuItem>
      {options.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: "1.4rem" }}>{l}</MenuItem>)}
    </TextField>
  );

  if (loadError) return <div className={cssClasses.empty}>{loadError}</div>;
  if (!terms) return <div className={cssClasses.empty}>Loading…</div>;

  const pointsWithTerms = points.filter((p) => terms.some((t) => t.pointKey === p.key));

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
          {chip(`${live} Live`, "#027A48", "#ECFDF3")}
          {chip(`${approved} Legal Head approved`, "#B54708", "#FFFAEB")}
          {chip(`${terms.length - live - approved} Draft`, "#175CD3", "#EFF8FF")}
        </span>
        {canEdit && <Button style={btnPrimary} btnName="+ Add terms" clicked={openNew} />}
      </div>
      <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", margin: "1.2rem 0" }}>
        <TextField size="small" placeholder="Search terms" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} sx={{ minWidth: "22rem", ...inputSx }} />
        {select(filters.pointKey, (v) => setFilters({ ...filters, pointKey: v }), "Asked at", pointsWithTerms.map((p) => [p.key, p.name]))}
        {select(filters.status, (v) => setFilters({ ...filters, status: v }), "Status", Object.entries(STATUS))}
      </div>

      {shown.length === 0 ? (
        <div className={cssClasses.empty}>No terms match these filters.</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead>
              <tr>
                {["Terms", "Asked at", "How", "Who", "Asked", "Projects", "Version", "Status"].map((h) => <th key={h}>{h}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  <td>{pointName[t.pointKey] || t.pointKey}<p className={cssClasses.hint} style={{ margin: 0 }}>{t.pointKey}</p></td>
                  <td>{HOW[t.how]}</td>
                  <td>{WHO[t.who]}</td>
                  <td>{ASKED[t.asked]}</td>
                  <td>{t.projectIds.length ? t.projectIds.map((id) => projectName[id] || id).join(" · ") : "All CRM 2.0 projects"}</td>
                  <td>v{t.version}</td>
                  <td><Pill status={t.status} /></td>
                  <td style={{ width: "1%", textAlign: "right" }}>
                    <button type="button" className={cssClasses.pillEdit} onClick={() => openEdit(t)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className={cssClasses.hint}>
        Several terms can sit on one consent point; the customer sees them one after the other. Until a point has Live terms the customer sees nothing extra.
        Asked = once per customer (CUID), once per booking, or every time the step is done.
      </p>

      <Drawer anchor="right" open={!!panel} onClose={() => !busy && setPanel(null)} PaperProps={{ sx: { width: "min(64rem, 100vw)", padding: "2rem" } }}>
        {panel && (
          <TermPanel
            panel={panel} points={points} projects={projects} canEdit={canEdit} canApprove={canApprove} busy={busy}
            setTab={(tab) => setPanel((p) => ({ ...p, tab }))} setDraft={setDraft} onClose={() => setPanel(null)} onSave={save} onMove={move}
          />
        )}
      </Drawer>
    </div>
  );
}

function TermPanel({ panel, points, projects, canEdit, canApprove, busy, setTab, setDraft, onClose, onSave, onMove }) {
  const { term, draft, tab, errors } = panel;
  const status = term?.status || "DRAFT";
  const readOnly = !canEdit || busy;
  const err = (k) => errors?.[k] && <p className={cssClasses.error}>{errors[k]}</p>;
  const point = points.find((p) => p.key === draft.pointKey);
  const groups = [...new Set(points.filter((p) => p.isActive !== false).map((p) => p.groupName))];
  // Prototype kPh: Draft → Approve · Legal Head approved → Make Live · Live → Stop asking
  const step =
    !term ? null
      : status === "DRAFT" ? ["approve", "Approve", "Approved by the Legal Head"]
      : status === "APPROVED" ? ["live", "Make Live", `Live: ${term.title}`]
      : status === "LIVE" ? ["stop", "Stop asking", "Stopped asking. Acceptances already given are kept"]
      : null;
  const radios = (key, map) => (
    <RadioGroup value={draft[key]} onChange={(e) => setDraft({ [key]: e.target.value })}>
      {Object.entries(map).map(([v, l]) => (
        <FormControlLabel key={v} value={v} disabled={readOnly} control={<Radio size="small" />} label={<span className={cssClasses.radioText}>{l}</span>} />
      ))}
    </RadioGroup>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.6rem", height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p className={cssClasses.headSub} style={{ margin: 0 }}>{point?.name || "—"} · {draft.pointKey || "—"} · v{term?.version || 1}</p>
          <p className={cssClasses.sectionTitle} style={{ margin: 0 }}>{draft.title || "New terms"}</p>
        </div>
        <span className={cssClasses.modalClose} onClick={onClose} role="button" tabIndex={0} aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <span className={cssClasses.label}>Status</span> <Pill status={status} />
        {step && (
          <span style={{ marginLeft: "auto" }}>
            <Button style={step[0] === "stop" ? btnSecondary : btnPrimary} btnName={step[1]} disable={busy || !canApprove} clicked={() => onMove(step[0], step[2])} />
          </span>
        )}
        {!canApprove && <p className={cssClasses.hint} style={{ width: "100%", margin: 0 }}>The Legal Head approves and makes terms Live</p>}
      </div>

      <div style={{ display: "flex", gap: "0.8rem", borderBottom: "1px solid #EAECF0" }}>
        {PANEL_TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            style={{ border: 0, background: "none", padding: "0.8rem 0.4rem", fontSize: "1.4rem", cursor: "pointer", color: t === tab ? "#155EEF" : "#667085", borderBottom: t === tab ? "2px solid #155EEF" : "2px solid transparent" }}>
            {t}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.4rem" }}>
        {tab === "Terms text" && (
          <>
            <div>
              <p className={cssClasses.label}>Title <span className={cssClasses.required}>*</span></p>
              <TextField fullWidth size="small" value={draft.title} disabled={readOnly} onChange={(e) => setDraft({ title: e.target.value })} sx={inputSx} />
              <p className={cssClasses.hint}>Shown as the heading of the pop-up</p>
              {err("title")}
            </div>
            <div>
              <p className={cssClasses.label}>Terms text <span className={cssClasses.required}>*</span></p>
              <CKEditor editor={ClassicEditor} config={{ toolbar: EDITOR_TOOLBAR }} data={draft.content} disabled={readOnly}
                onChange={(event, editor) => setDraft({ content: editor.getData() })} />
              <p className={cssClasses.hint}>Bold, lists and links as today's Customer Consent editor. Changing the text of approved terms makes a new version (Draft) for the Legal Head.</p>
              {err("content")}
            </div>
            <div className={cssClasses.card}>
              <p className={cssClasses.label} style={{ marginTop: 0 }}>Preview · Customer Portal pop-up</p>
              <p className={cssClasses.sectionTitle} style={{ margin: 0 }}>{draft.title || "No title"}</p>
              <div style={{ fontSize: "1.4rem" }} dangerouslySetInnerHTML={{ __html: draft.content || "<p>No text yet</p>" }} />
              <label style={{ display: "flex", gap: "0.8rem", fontSize: "1.3rem", alignItems: "center" }}><Checkbox size="small" disabled checked={false} /> I agree to the terms above</label>
              {draft.how === "TICK_OTP" && (
                <p className={cssClasses.hint}>
                  Then: OTP to {draft.who === "CONTACT_PERSON" ? "the portal contact person's" : draft.who === "PRIMARY_APPLICANT" ? "the primary applicant's" : "each applicant's"} mobile (6 digits, checked on the server)
                </p>
              )}
            </div>
          </>
        )}

        {tab === "Where and who" && (
          <>
            <div>
              <p className={cssClasses.label}>Asked at (consent point) <span className={cssClasses.required}>*</span></p>
              <RadioGroup value={draft.pointKey} onChange={(e) => setDraft({ pointKey: e.target.value })}>
                {groups.map((g) => (
                  <div key={g} style={{ marginBottom: "0.8rem" }}>
                    <p className={cssClasses.hint} style={{ margin: "0.4rem 0" }}>{g}</p>
                    {points.filter((p) => p.groupName === g && p.isActive !== false).map((p) => (
                      <FormControlLabel key={p.key} value={p.key} disabled={readOnly} control={<Radio size="small" />} label={<span className={cssClasses.radioText}>{p.name}</span>} />
                    ))}
                  </div>
                ))}
              </RadioGroup>
              {point && <p className={cssClasses.hint}>Customer Portal: {point.portalScreen} · the customer accepts before the step goes ahead</p>}
              {err("pointKey")}
            </div>
            <div><p className={cssClasses.label}>How</p>{radios("how", HOW)}{err("how")}</div>
            <div>
              <p className={cssClasses.label}>Who</p>{radios("who", WHO)}
              <p className={cssClasses.hint}>Before the Agreement KYC there are no applicants yet: use Portal contact person</p>{err("who")}
            </div>
            <div>
              <p className={cssClasses.label}>Asked</p>{radios("asked", ASKED)}
              <p className={cssClasses.hint}>Once per customer = the same CUID is not asked again on another booking</p>{err("asked")}
            </div>
          </>
        )}

        {tab === "Projects and version" && (
          <>
            <div>
              <p className={cssClasses.label}>Projects</p>
              <div className={cssClasses.switchLine}>
                <Switch checked={!draft.projectIds?.length} disabled={readOnly}
                  onChange={(e) => setDraft({ projectIds: e.target.checked ? [] : projects.slice(0, 1).map((p) => p.projectId) })} />
                <span className={cssClasses.radioText}>Ask on all CRM 2.0 projects</span>
              </div>
              <p className={cssClasses.hint}>Off: pick the projects.</p>
              {!!draft.projectIds?.length && projects.map((p) => (
                <FormControlLabel key={p.projectId} disabled={readOnly}
                  control={<Checkbox size="small" checked={draft.projectIds.includes(p.projectId)}
                    onChange={(e) => setDraft({ projectIds: e.target.checked ? [...draft.projectIds, p.projectId] : draft.projectIds.filter((x) => x !== p.projectId) })} />}
                  label={<span className={cssClasses.radioText}>{p.projectName}</span>} />
              ))}
              {err("projectIds")}
            </div>
            <div>
              <p className={cssClasses.label}>Version</p>
              <p className={cssClasses.headSub}>Now v{term?.version || 1} · <Pill status={status} /></p>
              <div className={cssClasses.switchLine}>
                <Switch checked={!!draft.askAgain} disabled={readOnly} onChange={(e) => setDraft({ askAgain: e.target.checked })} />
                <span className={cssClasses.radioText}>Ask again customers who agreed earlier</span>
              </div>
              <p className={cssClasses.hint}>When a new version goes Live. Off: only customers who have not accepted yet see the new version.</p>
            </div>
          </>
        )}

        {term && (
          <div>
            <p className={cssClasses.label}>History</p>
            <div className={cssClasses.tableCard}>
              <table className={cssClasses.table}>
                <thead><tr><th>When</th><th>Who</th><th>What changed</th></tr></thead>
                <tbody>
                  {term.history.slice().reverse().map((h, i) => (
                    <tr key={i}><td>{dayjs(h.at).format("DD MMM YYYY, hh:mm A")}</td><td>{h.byName || h.by}</td><td>{h.note}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className={cssClasses.saveBar}>
        <Button style={btnSecondary} btnName="Cancel" disable={busy} clicked={onClose} />
        <Button style={btnPrimary} btnName="Save" disable={busy || !canEdit} isLoading={busy} clicked={onSave} />
      </div>
    </div>
  );
}

export default Crm2ConsentTerms;
